import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { Formik } from "formik";
import { MultiValue, SingleValue } from "react-select";
import { AppDispatch } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  clearToggleUserStatusState,
  clearUpdateUserState,
  getToggleUserStatusState,
  getUpdateUserState,
  getUserByIdState,
  ToggleUserStatusRequest,
  toggleUserStatusStateAction,
  UpdateUserRequest,
  updateUserStateAction,
  userByIdStateAction,
  UserRole,
  UserStatus,
  UserWorkingTime,
} from "../../../redux/features/user-slice";
import paths from "../../../navigation/paths";
import Input from "../../../components/input";
import {
  CommonStatus,
  getGroupTeamState,
  getPositionsState,
  getTeamState,
  GroupTeamResponse,
  groupTeamStateAction,
  PositionResponse,
  positionsStateAction,
  TeamResponse,
  teamStateAction,
} from "../../../redux/features/other-slice";
import { isSelectSingleValue, isValidTime } from "../../../core/utils";
import CheckBox from "../../../components/checkbox";
import Button from "../../../components/button";
import { MinusOutlined } from "../../../components/icons/minus-outlined";
import { AddOutlined } from "../../../components/icons/add-outlined";
import { adminCat, mgTeam } from "../../../core/utils/constants";
import {
  ExperienceResponse,
  experiencesStateAction,
  getExperiencesState,
} from "../../../redux/features/experience-slice";
import {
  CustomerResponse,
  allCustomersStateAction,
  getAllCustomersState,
  CustomerStatus,
} from "../../../redux/features/customer-slice";
import {
  categoriesStateAction,
  CategoryResponse,
  getCategoriesState,
} from "../../../redux/features/category-slice";

export default function UserDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const teamsState = useSelector(getTeamState);
  const groupTeamsState = useSelector(getGroupTeamState);
  const positionsState = useSelector(getPositionsState);
  const experiencesState = useSelector(getExperiencesState);
  const customersState = useSelector(getAllCustomersState);
  const categoriesState = useSelector(getCategoriesState);
  const userState = useSelector(getUserByIdState).data;
  const updateUserState = useSelector(getUpdateUserState);
  const toggleUserStatusState = useSelector(getToggleUserStatusState);
  const errorState = useSelector(getErrorState);

  const [newWorkingTime, setNewWorkingTime] = useState<UserWorkingTime>({
    startTime: "00:00",
    endTime: "00:00",
  });

  const fetchUserDetail = useCallback(async () => {
    if (id) {
      await dispatch(userByIdStateAction({ id }));
    }
  }, [dispatch, id]);

  const submitUpdateUser = async (values: UpdateUserRequest) => {
    await dispatch(updateUserStateAction(values));
  };

  const submitToggleUserStatus = async (values: ToggleUserStatusRequest) => {
    await dispatch(toggleUserStatusStateAction(values));
  };

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  const renderRoleItem = (
    role: UserRole,
    des: string,
    checked: boolean,
    onChange: (event: ChangeEvent<HTMLInputElement>) => void
  ) => {
    return (
      <li className="text-sm flex items-center space-x-2">
        <CheckBox checked={checked} onChange={onChange} />
        <div>
          <label>{role.toString().capitalize()}</label>
          <p className="italic text-text-title text-xs">{des}</p>
        </div>
      </li>
    );
  };

  const fetchPositions = async (teamId: string) => {
    await dispatch(
      positionsStateAction({
        teamId,
        status: [CommonStatus.Active],
      })
    );
  };

  useEffect(() => {
    fetchUserDetail();
    const fetchInitialData = async () => {
      await dispatch(
        teamStateAction({
          roles: [0, 1],
          status: [CommonStatus.Active, CommonStatus.Inactive],
        })
      );
      await dispatch(
        groupTeamStateAction({
          status: [CommonStatus.Active, CommonStatus.Inactive],
        })
      );
      await dispatch(
        experiencesStateAction({
          status: [CommonStatus.Active, CommonStatus.Inactive],
        })
      );
      await dispatch(
        allCustomersStateAction({
          status: [CustomerStatus.Active, CustomerStatus.Inactive],
        })
      );
      await dispatch(
        categoriesStateAction({
          status: [CommonStatus.Active, CommonStatus.Inactive],
        })
      );
    };
    fetchInitialData();
  }, [fetchUserDetail]);

  useEffect(() => {
    if (userState) {
      fetchPositions(userState.team.id);
      dispatch(
        setBreadcrumbState([
          { id: paths.USERS, path: null, title: "Users" },
          {
            id: userState.team.id,
            path: -1,
            title: userState.team.name,
          },
          { id: userState.id, path: null, title: userState.name },
        ])
      );
    }
  }, [userState]);

  useEffect(() => {
    if (updateUserState.data && updateUserState.data.success) {
      handleSuccessToast(updateUserState.data.message, clearUpdateUserState);
      fetchUserDetail();
    }
  }, [updateUserState, handleSuccessToast]);

  useEffect(() => {
    if (toggleUserStatusState.data && toggleUserStatusState.data.success) {
      handleSuccessToast(
        toggleUserStatusState.data.message,
        clearToggleUserStatusState
      );
      fetchUserDetail();
    }
  }, [toggleUserStatusState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Supervisor]}
    >
      {userState && (
        <>
          <Helmet title={userState.name} />
          <Formik
            initialValues={{
              name: userState.name,
              email: userState.email,
              positionId: userState.position.id,
              experienceId: userState.experience.id,
              customerId: userState.customer?.id ?? null,
              categoryId: userState.category?.id ?? null,
              role: userState.role,
              misaCode: userState.misaCode,
              teamId: userState.team.id,
              groupTeamId: userState.groupTeam?.id ?? null,
              workingTimes: userState.workingTimes,
            }}
            validationSchema={Yup.object().shape({
              name: Yup.string().required("Name is required"),
              email: Yup.string()
                .email("Must be a valid email")
                .max(50)
                .required("Email is required"),
              positionId: Yup.string().required("Position is required"),
              experienceId: Yup.string().required("Experience is required"),
              teamId: Yup.string().required("Location is required"),
              workingTimes: Yup.array().min(1, "Working Times is required"),
            })}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              submitUpdateUser({
                ...values,
                id: userState.id,
                misaCode: undefined,
              } as UpdateUserRequest);
            }}
          >
            {({
              values,
              errors,
              handleChange,
              handleSubmit,
              isSubmitting,
              setValues,
              dirty,
            }) => (
              <form
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                className="h-full overflow-y-auto"
              >
                <div
                  className="h-full grid grid-cols-2 gap-8 overflow-y-scroll"
                  style={{
                    height: "calc(100% - 56px)",
                  }}
                >
                  <div className="space-y-4">
                    <Input
                      label="Name *"
                      type="text"
                      id="name"
                      value={values.name}
                      onChange={handleChange}
                      errorText={errors.name}
                    />
                    <Input
                      label="Email *"
                      type="email"
                      id="email"
                      value={values.email}
                      onChange={handleChange}
                      errorText={errors.email}
                    />
                    <div className="flex flex-col">
                      <label className="text-xs text-text-title">Team</label>
                      <StyledSelect
                        loading={groupTeamsState.loading}
                        options={groupTeamsState.data.map(
                          (team: GroupTeamResponse) => ({
                            value: team.id,
                            label: team.name,
                          })
                        )}
                        menuPosition="fixed"
                        value={
                          values.groupTeamId !== null
                            ? (() => {
                                const index = groupTeamsState.data.findIndex(
                                  (e) => e.id === values.groupTeamId
                                );
                                return index !== -1
                                  ? {
                                      value: values.groupTeamId,
                                      label: groupTeamsState.data[index].name,
                                    }
                                  : {
                                      value: "",
                                      label: "Select...",
                                    };
                              })()
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              groupTeamId: value.value as string,
                            });
                          } else {
                            setValues({
                              ...values,
                              groupTeamId: null,
                            });
                          }
                        }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-text-title">
                        Location *
                      </label>
                      <StyledSelect
                        loading={teamsState.loading}
                        options={teamsState.data.map((team: TeamResponse) => ({
                          value: team.id,
                          label: team.name,
                        }))}
                        isClearable={false}
                        menuPosition="fixed"
                        value={
                          values.teamId !== ""
                            ? (() => {
                                const index = teamsState.data.findIndex(
                                  (e) => e.id === values.teamId
                                );
                                return index !== -1
                                  ? {
                                      value: values.teamId,
                                      label: teamsState.data[index].name,
                                    }
                                  : {
                                      value: "",
                                      label: "Select...",
                                    };
                              })()
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            fetchPositions(value.value as string);
                            setValues({
                              ...values,
                              teamId: value.value as string,
                              positionId: "",
                            });
                          }
                        }}
                      />
                      {errors.teamId && (
                        <p className="text-error-main text-xs">
                          {errors.teamId}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-text-title">
                        Position *
                      </label>
                      <StyledSelect
                        loading={positionsState.loading}
                        options={positionsState.data.map(
                          (pos: PositionResponse) => ({
                            value: pos.id,
                            label: pos.name,
                          })
                        )}
                        isClearable={false}
                        menuPosition="fixed"
                        value={
                          values.positionId !== ""
                            ? (() => {
                                const index = positionsState.data.findIndex(
                                  (e) => e.id === values.positionId
                                );
                                return index !== -1
                                  ? {
                                      value: values.positionId,
                                      label: positionsState.data[index].name,
                                    }
                                  : {
                                      value: "",
                                      label: "Select...",
                                    };
                              })()
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              positionId: value.value as string,
                            });
                          }
                        }}
                      />
                      {errors.positionId && (
                        <p className="text-error-main text-xs">
                          {errors.positionId}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-text-title">
                        Experience *
                      </label>
                      <StyledSelect
                        loading={experiencesState.loading}
                        options={experiencesState.data.map(
                          (exp: ExperienceResponse) => ({
                            value: exp.id,
                            label: exp.title,
                          })
                        )}
                        isClearable={false}
                        menuPosition="fixed"
                        value={
                          values.experienceId !== ""
                            ? (() => {
                                const index = experiencesState.data.findIndex(
                                  (e) => e.id === values.experienceId
                                );
                                return index !== -1
                                  ? {
                                      value: values.experienceId,
                                      label: experiencesState.data[index].title,
                                    }
                                  : {
                                      value: "",
                                      label: "Select...",
                                    };
                              })()
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              experienceId: value.value as string,
                            });
                          }
                        }}
                      />
                      {errors.experienceId && (
                        <p className="text-error-main text-xs">
                          {errors.experienceId}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-text-title">
                        Customer
                      </label>
                      <StyledSelect
                        loading={customersState.loading}
                        options={customersState.data.map(
                          (cus: CustomerResponse) => ({
                            value: cus.id,
                            label: cus.name,
                          })
                        )}
                        menuPosition="fixed"
                        value={
                          values.customerId !== null
                            ? (() => {
                                const index = customersState.data.findIndex(
                                  (cus: CustomerResponse) => cus.id === values.customerId
                                );
                                return index !== -1
                                  ? {
                                      value: values.customerId,
                                      label: customersState.data[index].name,
                                    }
                                  : {
                                      value: "",
                                      label: "Select...",
                                    };
                              })()
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              customerId: value.value as string,
                            });
                          } else {
                            setValues({
                              ...values,
                              customerId: null,
                            });
                          }
                        }}
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-text-title">
                        Category *
                      </label>
                      <StyledSelect
                        loading={categoriesState.loading}
                        options={categoriesState.data.map(
                          (cat: CategoryResponse) => ({
                            value: cat.id,
                            label: cat.title,
                          })
                        )}
                        isClearable={false}
                        menuPosition="fixed"
                        value={
                          values.categoryId !== null
                            ? (() => {
                                const index = categoriesState.data.findIndex(
                                  (e) => e.id === values.categoryId
                                );
                                return index !== -1
                                  ? {
                                      value: values.categoryId,
                                      label: categoriesState.data[index].title,
                                    }
                                  : {
                                      value: "",
                                      label: "Select...",
                                    };
                              })()
                            : { value: "", label: "Select..." }
                        }
                        onChange={(
                          value:
                            | SingleValue<OptionValue>
                            | MultiValue<OptionValue>
                        ) => {
                          if (value && isSelectSingleValue(value)) {
                            setValues({
                              ...values,
                              categoryId: value.value as string,
                            });
                          }
                        }}
                      />
                    </div>
                    <Input
                      label="Misa Code (Read-only)"
                      type="text"
                      id="misaCode"
                      readonly
                      value={values.misaCode}
                      onChange={handleChange}
                      errorText={errors.misaCode}
                    />
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-xs text-text-title">Role</label>
                      <ul className="space-y-2">
                        {renderRoleItem(
                          UserRole.Admin,
                          "Full access",
                          values.role === UserRole.Admin,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.Admin })
                        )}
                        {renderRoleItem(
                          UserRole.Supervisor,
                          "View all data and reports organization-wide",
                          values.role === UserRole.Supervisor,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.Supervisor })
                        )}
                        {renderRoleItem(
                          UserRole.Manager,
                          "Manage leaders, contributors, members and tasks",
                          values.role === UserRole.Manager,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.Manager })
                        )}
                        {renderRoleItem(
                          UserRole.Leader,
                          "Manage contributors, members and tasks",
                          values.role === UserRole.Leader,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.Leader })
                        )}
                        {renderRoleItem(
                          UserRole.Contributor,
                          "Create and configure tasks only",
                          values.role === UserRole.Contributor,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.Contributor })
                        )}
                        {renderRoleItem(
                          UserRole.Member,
                          "Submit reports and view personal tasks",
                          values.role === UserRole.Member,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.Member })
                        )}
                        {renderRoleItem(
                          UserRole.CustomerSupport,
                          "Customer support",
                          values.role === UserRole.CustomerSupport,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.CustomerSupport })
                        )}
                        {renderRoleItem(
                          UserRole.Trainer,
                          "Manage courses",
                          values.role === UserRole.Trainer,
                          (event: ChangeEvent<HTMLInputElement>) =>
                            setValues({ ...values, role: UserRole.Trainer })
                        )}
                      </ul>
                    </div>
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-xs text-text-title">
                        Working Times
                      </label>
                      {values.workingTimes.map((time: UserWorkingTime) => (
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            <div className="w-1/2 flex items-center justify-between border border-divider p-2 rounded-lg">
                              <label className="text-sm">
                                {time.startTime}
                              </label>
                              <hr className="flex-1 border border-dashed border-divider mx-2" />
                              <label className="text-sm">{time.endTime}</label>
                            </div>
                            <div
                              className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                              onClick={() => {
                                if (values.workingTimes.length > 1) {
                                  setValues({
                                    ...values,
                                    workingTimes: values.workingTimes.filter(
                                      (e) =>
                                        e.startTime !== time.startTime &&
                                        e.endTime !== time.endTime
                                    ),
                                  });
                                }
                              }}
                            >
                              <MinusOutlined
                                className={`h-5 w-5 ${
                                  values.workingTimes.length > 1
                                    ? "text-error-main"
                                    : "text-action-disabled"
                                }`}
                              />
                            </div>
                          </div>
                          <label className="text-xs text-text-title italic py-2">
                            or
                          </label>
                        </div>
                      ))}
                      <div className="flex items-center space-x-2">
                        <div className="w-1/2 flex items-center space-x-2">
                          <Input
                            type="text"
                            value={newWorkingTime.startTime}
                            onChange={(event) =>
                              setNewWorkingTime({
                                ...newWorkingTime,
                                startTime: event.target.value,
                              })
                            }
                            exClassName="text-center"
                          />
                          <label className="text-xs text-text-title">to</label>
                          <Input
                            type="text" // Fixed typo: "tetx" -> "text"
                            value={newWorkingTime.endTime}
                            onChange={(event) =>
                              setNewWorkingTime({
                                ...newWorkingTime,
                                endTime: event.target.value,
                              })
                            }
                            exClassName="text-center"
                          />
                        </div>
                        <div
                          className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                          onClick={() => {
                            if (
                              isValidTime(newWorkingTime.startTime) &&
                              isValidTime(newWorkingTime.endTime)
                            ) {
                              setValues({
                                ...values,
                                workingTimes: [
                                  ...values.workingTimes,
                                  newWorkingTime,
                                ],
                              });
                            }
                          }}
                        >
                          <AddOutlined
                            className={`w-5 h-5 ${
                              isValidTime(newWorkingTime.startTime) &&
                              isValidTime(newWorkingTime.endTime)
                                ? "text-success-main"
                                : "text-action-disabled"
                            } `}
                          />
                        </div>
                      </div>
                      {errors.workingTimes && (
                        <p className="text-error-main text-xs">
                          {errors.workingTimes as string}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4 pt-4">
                  <Button
                    label="Save Changes"
                    type="submit"
                    loading={
                      updateUserState.loading || toggleUserStatusState.loading
                    }
                    disabled={!dirty}
                  />
                  <Button
                    label={
                      userState.status === UserStatus.Active
                        ? "Deactive User"
                        : "Active User"
                    }
                    variant="outlined"
                    type="button"
                    loading={
                      updateUserState.loading || toggleUserStatusState.loading
                    }
                    onClick={() =>
                      submitToggleUserStatus({
                        id: userState.id,
                        status:
                          userState.status === UserStatus.Active
                            ? UserStatus.Inactive
                            : UserStatus.Active,
                      })
                    }
                    exClassName={
                      userState.status === UserStatus.Active
                        ? "text-error-main border border-error-main"
                        : ""
                    }
                  />
                </div>
              </form>
            )}
          </Formik>
        </>
      )}
      <ToastComponent id="userDetail" />
    </PermissionGate>
  );
}